<template>
  <div
    ref="modalRef"
    class="modal-overlay"
    @click.self="close"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="modal-content"
      :class="{ 'modal-content--show-overflow': showOverflow }"
    >
      <slot></slot>
      <button
        v-if="showCloseButton"
        aria-label="Lukk"
        class="close-button"
        @click="close"
      >
        <svg class="modal-content__close">
          <use :xlink:href="`#${cancel}`" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, watch } from 'vue'
  import cancel from '@/assets/icons/cancel.svg'
  import { useTabTrap } from '@/composables/useTabTrap'

  export default defineComponent({
    name: 'Modal',
    emits: ['close', 'update:modelValue'],
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      showCloseButton: {
        type: Boolean,
        default: true,
      },
      showOverflow: {
        type: Boolean,
        default: false,
      }
    },
    setup(_, { emit }) {
      const modalRef = ref<HTMLElement | null>(null)
      const { initTabTrap, cleanupTabTrap } = useTabTrap(modalRef)

      const close = () => {
        cleanupTabTrap()
        document.body.classList.remove('no-scroll')
        emit('close')
      }

      watch(() => modalRef.value, (newValue) => {
        if (newValue) {
          initTabTrap()
          document.body.classList.add('no-scroll')
        }
      })

      return {
        modalRef,
        close,
        cancel,
      }
    },
    beforeUnmount() {
      document.body.classList.remove('no-scroll')
    },
  })
</script>

<style lang="scss" scoped>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-content {
    position: relative;
    background-color: white;
    padding: rem(24) 24px;
    border-radius: 8px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;

    &--show-overflow {
      overflow: visible;
    }
  }

  .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;

    &:focus {
      outline: 2px solid $clr-primary;
      border-radius: 4px;
    }
  }

  .modal-content__close {
    width: 24px;
    height: 24px;
    fill: $clr-text;
  }
</style>
